import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import KodaCareLogoFull from '../../src/components/Custom Icons/KodaCareLogoFull';
import { useAuth } from '../../src/context/AuthContext';
import { useLanguage } from '../../src/functions/useLanguage';
import { wt } from '../i18n';
import { websitePath } from '../lib/routes';

export function Navbar() {
  const { isAuthenticated } = useAuth();
  const { language, t: ta } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const appHost = import.meta.env.VITE_APP_HOST || 'https://app.kodacare.pet';
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Close mobile menu on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (mobileOpen && navRef.current && !navRef.current.contains(e.target as Node)) {
        setMobileOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [mobileOpen]);

  const t = (key: Parameters<typeof wt>[1]) => wt(language, key);

  const scrollToId = (id: string) => {
    setMobileOpen(false);
    const path = location.pathname.replace(/^\/website/, '') || '/';
    if (path !== '/') {
      navigate(websitePath('/'));
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <nav ref={navRef} className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled || mobileOpen ? 'bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-sm' : 'bg-transparent border-b border-transparent'}`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-16">
        {/* Logo */}
        <button
          onClick={() => scrollToId('hero')}
          className="flex items-left p-0 text-primary-brown"
          aria-label="KodaCare home"
        >
          <KodaCareLogoFull style={{ height: 28, width: 'auto' }} />
        </button>

        {/* Right actions */}
        <div className="flex items-center gap-3">
          {/* Desktop nav links */}
          <div className="hidden md:flex items-center gap-8 mr-[50px]">
            <button onClick={() => scrollToId('features')} className="text-xs font-bold tracking-widest text-gray-700 hover:text-primary-brown transition-colors">{t('nav.features')}</button>
            <button onClick={() => scrollToId('pricing')} className="text-xs font-bold tracking-widest text-gray-700 hover:text-primary-brown transition-colors">{t('nav.pricing')}</button>
            <button onClick={() => navigate(websitePath('/blog'))} className="text-xs font-bold tracking-widest text-gray-700 hover:text-primary-brown transition-colors uppercase">{t('nav.blog')}</button>
          </div>

          {/* Unauthenticated: Login (both desktop and mobile) */}
          {!isAuthenticated && (
            <button
              onClick={() => window.location.href = `${appHost}/login`}
              className="text-sm font-medium text-gray-700 hover:text-primary-brown transition-colors px-2"
            >
              {t('nav.login')}
            </button>
          )}

          {/* Unauthenticated desktop: Sign Up CTA */}
          {!isAuthenticated && (
            <button
              onClick={() => window.location.href = `${appHost}/register`}
              className="hidden md:block text-sm font-bold bg-primary-brown hover:bg-primary-brown/80 text-white px-5 py-2 rounded-full transition-colors shadow-sm"
            >
              {t('nav.signup')}
            </button>
          )}

          {/* Authenticated desktop: Dashboard */}
          {isAuthenticated && (
            <div className="hidden md:flex items-center gap-3">
              <button
                onClick={() => navigate('/records')}
                className="text-sm font-bold bg-primary-brown hover:bg-primary-brown/80 text-white px-5 py-2 rounded-full transition-colors shadow-sm"
              >
                {ta('account.openDashboard')}
              </button>
            </div>
          )}

          {/* Authenticated mobile: burger */}
          {isAuthenticated && (
            <button
              className="md:hidden w-10 h-10 grid place-items-center text-gray-700"
              onClick={() => setMobileOpen(o => !o)}
              aria-label="Open menu"
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          )}
        </div>
      </div>

      {/* Mobile menu — authenticated only */}
      {isAuthenticated && mobileOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 px-6 py-4 flex flex-col gap-1">
          <button onClick={() => scrollToId('features')} className="text-left text-sm font-bold tracking-widest text-gray-700 hover:text-primary-brown py-3 border-b border-gray-50">{t('nav.features')}</button>
          <button onClick={() => scrollToId('pricing')} className="text-left text-sm font-bold tracking-widest text-gray-700 hover:text-primary-brown py-3 border-b border-gray-50">{t('nav.pricing')}</button>
          <button onClick={() => { navigate(websitePath('/blog')); setMobileOpen(false); }} className="text-left text-sm font-bold tracking-widest text-gray-700 hover:text-primary-brown py-3 border-b border-gray-50 uppercase">{t('nav.blog')}</button>
          <button onClick={() => { navigate('/records'); setMobileOpen(false); }} className="text-left text-sm font-bold text-primary-brown py-3">{ta('account.openDashboard')}</button>
        </div>
      )}
    </nav>
  );
}
